import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { getAllCoupons } from "../store/actions/couponActions";
import { Container } from "../components/layout/Container";
import CouponCard from "../components/cards/CouponCard";
import useSEO from "../hooks/useSEO";
import { generateCollectionPageSchema } from "../utils/schemaGenerators";
import seoConfig from "../config/seoConfig";

const Hero = styled.section`
  background: hsl(${({ theme }) => theme.colors.navDark});
  padding: clamp(2rem, 4vw, 3.25rem) 0;
  text-align: center;
`;

const H1 = styled.h1`
  margin: 0 0 0.5rem;
  font-size: ${({ theme }) => theme.font.heroTitle};
  font-weight: 800;
  color: hsl(${({ theme }) => theme.colors.navDarkForeground});
`;

const Lead = styled.p`
  margin: 0 auto;
  max-width: min(100%, 52rem);
  color: hsl(${({ theme }) => theme.colors.navDarkForeground} / 0.65);
  font-size: clamp(0.9375rem, 0.88rem + 0.25vw, 1.125rem);
`;

const Main = styled.div`
  padding: 2rem 0 3rem;
`;

const Stack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const LoadNote = styled.p`
  text-align: center;
  padding: 2rem 1rem;
  color: hsl(${({ theme }) => theme.colors.mutedForeground});
`;

const EmptyNote = styled(LoadNote)`
  padding: 3rem 1rem;
`;

export default function CouponsPage() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const coupons = useSelector((s) => s.coupon.coupons) || [];
  const couponError = useSelector((s) => s.coupon.error);
  const loading = useSelector((s) => s.coupon.loading);

  useEffect(() => {
    dispatch(getAllCoupons());
  }, [dispatch]);

  const seo = useSEO({
    schema: generateCollectionPageSchema({
      name: "All Coupons",
      description: "Browse all active coupon codes and deals.",
      url: `${seoConfig.siteUrl}/coupons`,
      numberOfItems: coupons.length,
    }),
  });

  return (
    <>
      {seo}
      <Hero>
        <Container>
          <H1>All coupons &amp; deals</H1>
          <Lead>
            Browse every active code, printable offer, and sale in one place.
            Use the search bar above to narrow results by store or keyword.
          </Lead>
        </Container>
      </Hero>
      <Main>
        <Container>
          {loading && <LoadNote>{t("coupon.loading")}</LoadNote>}
          {!loading && couponError && (
            <LoadNote style={{ color: "hsl(0 84% 60%)" }}>{couponError}</LoadNote>
          )}
          {!loading && !couponError && coupons.length === 0 && (
            <EmptyNote>{t("coupon.emptyList")}</EmptyNote>
          )}
          <Stack>
            {coupons.map((c) => (
              <CouponCard key={c.id} coupon={c} />
            ))}
          </Stack>
        </Container>
      </Main>
    </>
  );
}
